import React, { useState, createContext, useContext } from 'react';
interface WizardStep1Data {
  storeName: string;
  organizationId: string;
  slug: string;
  supportEmail: string;
  supportPhone: string;
  internalNotes: string;
}
interface WizardStep2Data {
  isPublished: boolean;
  orderingEnabled: boolean;
  scheduleEnabled: boolean;
  openDate: string;
  openTime: string;
  closeDate: string;
  closeTime: string;
  timezone: string;
  closingSoonThreshold: string;
  forceOpen: boolean;
  forceClosed: boolean;
  pauseOrdering: boolean;
  comingSoonMessage: string;
  openWindowMessage: string;
  closedMessage: string;
  estimatedTimeline: string;
}
interface WizardStep3Data {
  accessType: 'public' | 'password' | 'code';
  password: string;
  accessCode: string;
  allowedEmails: string;
}
interface WizardStep4Data {
  products: any[];
  bundles: any[];
}
interface WizardStep5Data {
  fulfillmentMode: 'ship-to-home' | 'bulk-ship' | 'hybrid';
  bulkShippingAddress: string;
  requireEmail: boolean;
  collectPhone: boolean;
  collectStudentName: boolean;
  collectGradeTeacher: boolean;
  allowInvoice: boolean;
}
interface WizardData {
  step1: WizardStep1Data;
  step2: WizardStep2Data;
  step3: WizardStep3Data;
  step4: WizardStep4Data;
  step5: WizardStep5Data;
}
interface WizardContextType {
  data: WizardData;
  updateStep1: (data: Partial<WizardStep1Data>) => void;
  updateStep2: (data: Partial<WizardStep2Data>) => void;
  updateStep3: (data: Partial<WizardStep3Data>) => void;
  updateStep4: (data: Partial<WizardStep4Data>) => void;
  updateStep5: (data: Partial<WizardStep5Data>) => void;
  resetWizard: () => void;
}
const WizardContext = createContext<WizardContextType | undefined>(undefined);
const initialData: WizardData = {
  step1: {
    storeName: '',
    organizationId: '',
    slug: '',
    supportEmail: 'support@printer.com',
    supportPhone: '',
    internalNotes: ''
  },
  step2: {
    isPublished: false,
    orderingEnabled: false,
    scheduleEnabled: true,
    openDate: '',
    openTime: '09:00',
    closeDate: '',
    closeTime: '23:59',
    timezone: 'America/Chicago',
    closingSoonThreshold: '48',
    forceOpen: false,
    forceClosed: false,
    pauseOrdering: false,
    comingSoonMessage: 'Get ready! Store opens soon.',
    openWindowMessage: 'Order now before the store closes!',
    closedMessage: 'This store is now closed.',
    estimatedTimeline: 'Orders will be produced and shipped approximately 2 weeks after the store closes.'
  },
  step3: {
    accessType: 'public',
    password: '',
    accessCode: '',
    allowedEmails: ''
  },
  step4: {
    products: [],
    bundles: []
  },
  step5: {
    fulfillmentMode: 'ship-to-home',
    bulkShippingAddress: '',
    requireEmail: true,
    collectPhone: false,
    collectStudentName: true,
    collectGradeTeacher: false,
    allowInvoice: false
  }
};
export function WizardProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [data, setData] = useState<WizardData>(initialData);
  const updateStep1 = (updates: Partial<WizardStep1Data>) => {
    setData(prev => ({
      ...prev,
      step1: {
        ...prev.step1,
        ...updates
      }
    }));
  };
  const updateStep2 = (updates: Partial<WizardStep2Data>) => {
    setData(prev => ({
      ...prev,
      step2: {
        ...prev.step2,
        ...updates
      }
    }));
  };
  const updateStep3 = (updates: Partial<WizardStep3Data>) => {
    setData(prev => ({
      ...prev,
      step3: {
        ...prev.step3,
        ...updates
      }
    }));
  };
  const updateStep4 = (updates: Partial<WizardStep4Data>) => {
    setData(prev => ({
      ...prev,
      step4: {
        ...prev.step4,
        ...updates
      }
    }));
  };
  const updateStep5 = (updates: Partial<WizardStep5Data>) => {
    setData(prev => ({
      ...prev,
      step5: {
        ...prev.step5,
        ...updates
      }
    }));
  };
  const resetWizard = () => {
    setData(initialData);
  };
  return <WizardContext.Provider value={{
    data,
    updateStep1,
    updateStep2,
    updateStep3,
    updateStep4,
    updateStep5,
    resetWizard
  }} data-id="element-591">
      {children}
    </WizardContext.Provider>;
}
export function useWizard() {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within WizardProvider');
  }
  return context;
}
export function useWizardOptional() {
  return useContext(WizardContext);
}